import React from 'react';
import { clsx } from 'clsx';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger } from 'src/components/ui/select';

interface AgentOption {
  id: number;
  email: string;
}

// Shared by several integration forms, so it is generic over the form's values.
export const AgentsSelect = <T extends FieldValues>({
  name = 'responder' as Path<T>,
  label,
  control,
  setValue,
  options = [],
}: {
  name?: Path<T>;
  label: React.ReactNode;
  control: Control<T>;
  // The picked agent's email is stored next to its id.
  setValue: (name: 'responder_email', value: string) => void;
  options?: AgentOption[];
}) => {
  const [open, setOpen] = React.useState(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        return (
          <label htmlFor={name} className={clsx(error && 'text-error')}>
            {label}
            <Select
              defaultValue={value}
              open={open}
              onOpenChange={setOpen}
              onValueChange={(val) => {
                const agent = options.find((option) => option.id === +val);

                setValue('responder_email', agent.email);
                onChange(val);
              }}
            >
              <SelectTrigger
                className={clsx(
                  'w-full border bg-white rounded-sm-5',
                  error ? 'border-error text-error' : 'border-[#E3E6E8]',
                )}
              >
                <div>
                  <p className={'text-sm font-medium first-letter:capitalize'}>
                    <span>
                      {options.find((option) => `${option.id}` === `${value}`)?.email || 'Email'}
                    </span>
                  </p>
                </div>
              </SelectTrigger>
              <SelectContent className={'bg-light'}>
                {options.map((option) => {
                  return (
                    <SelectItem
                      className={'px-4 rounded-none bg-white hover:!bg-[#a498ff1a]'}
                      key={option.id}
                      // Radix item values are typed as strings, but the numeric id is passed
                      // through as-is and ends up as the field value, so comparisons stringify both.
                      value={option.id as unknown as string}
                      indicatorHidden
                    >
                      <div className="flex flex-col">
                        <div className="flex gap-2 items-center">
                          <p
                            className={clsx(
                              'text-sm text-start relative first-letter:capitalize',
                              `${option.id}` === `${value}` ? 'font-semibold' : 'font-medium',
                            )}
                          >
                            <span>{option.email}</span>
                          </p>
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </label>
        );
      }}
    />
  );
};
